import { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Pencil, Check } from "lucide-react";
import { updateFavorite } from "../lib/api";

/**
 * Inline notes + tags editor for a saved favorite product.
 */
export default function FavoriteEditor({ favorite, onUpdated, rank }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(favorite.notes || "");
  const [tags, setTags] = useState(favorite.tags || []);
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const t = newTag.trim().toLowerCase().replace(/^#/, "");
    if (!t) return;
    if (tags.includes(t)) {
      setNewTag("");
      return;
    }
    setTags([...tags, t]);
    setNewTag("");
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateFavorite(favorite.id, { notes, tags });
      toast.success("Saved");
      setEditing(false);
      onUpdated?.(updated);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="mt-3" data-testid={`favorite-editor-${rank}`}>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {tags.map((t) => (
              <span
                key={t}
                className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 klein-bg text-white"
              >
                #{t}
              </span>
            ))}
          </div>
        )}
        {notes && (
          <p className="text-xs text-zinc-700 leading-relaxed mb-2 line-clamp-3 whitespace-pre-wrap">
            {notes}
          </p>
        )}
        <button
          onClick={() => setEditing(true)}
          data-testid={`edit-favorite-${rank}`}
          className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
        >
          <Pencil size={10} strokeWidth={2.5} />
          {notes || tags.length ? "Edit notes & tags" : "Add notes & tags"}
        </button>
      </div>
    );
  }

  return (
    <div
      className="mt-3 p-3 border border-zinc-900 bg-zinc-50"
      data-testid={`favorite-editor-open-${rank}`}
    >
      <label className="micro-label block mb-1 text-[9px]">Tags</label>
      <div className="flex flex-wrap gap-1 mb-2">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 klein-bg text-white"
          >
            #{t}
            <button
              onClick={() => removeTag(t)}
              className="hover:text-red-300"
              aria-label="Remove tag"
            >
              <X size={10} strokeWidth={3} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-1 mb-3">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="add tag..."
          data-testid={`tag-input-${rank}`}
          className="flex-1 px-2 py-1 text-[10px] font-mono border border-zinc-300 focus:outline-none focus:border-[#002FA7]"
        />
        <button
          onClick={addTag}
          type="button"
          data-testid={`add-tag-${rank}`}
          className="px-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white flex items-center"
        >
          <Plus size={12} strokeWidth={3} />
        </button>
      </div>

      <label className="micro-label block mb-1 text-[9px]">Notes</label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Ad copy ideas, competitor links, next steps..."
        rows={3}
        data-testid={`notes-input-${rank}`}
        className="w-full px-2 py-1.5 text-xs border border-zinc-300 focus:outline-none focus:border-[#002FA7] resize-none font-mono"
      />

      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid={`save-favorite-${rank}`}
          className="flex-1 bg-[#002FA7] text-white text-[10px] uppercase tracking-widest font-bold py-2 px-3 inline-flex items-center justify-center gap-1 hover:bg-[#001f70] disabled:opacity-50"
        >
          <Check size={12} strokeWidth={3} /> SAVE
        </button>
        <button
          onClick={() => {
            setEditing(false);
            setNotes(favorite.notes || "");
            setTags(favorite.tags || []);
            setNewTag("");
          }}
          data-testid={`cancel-edit-${rank}`}
          className="px-3 border border-zinc-900 text-[10px] uppercase tracking-widest font-bold hover:bg-zinc-100"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
